import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ActionApprove } from '../../../assets/icons/action-approve.svg';
import { ReactComponent as ActionReject } from '../../../assets/icons/action-reject.svg';
import { ReactComponent as StripePayoutsIcon } from '../../../assets/icons/sidebar/stripe-payout.svg';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  RequestStripePayoutsInterface,
  StripePayoutsInterface,
} from '../../../interface/stripe-payouts.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setPageTitle } from '../../../redux/slices/PageTitleSlice';
import {
  disableUnapprovedStripePayoutsApplied,
  fetchUnapprovedStripePayouts,
  fetchUnapprovedStripePayoutsMethodFilters,
  setSortData,
  updateUnapprovedStripePayoutsFilters,
  refreshUnapprovedStripePayouts,
} from '../../../redux/slices/UnapprovedStripePayoutsSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import { permissionCheck } from '../../../shared-functions/JWTDocodeFunction';
import { capitalizeFirstLetter } from '../../../shared-functions/StringFunctions';
import ToFixedValue from '../../../shared-functions/ToFixedValue';

import ApprovedContent from './ApprovedContent';
import RejectedContext from './RejectedContext';

interface ModalBoxDetail {
  title: string;
  content: JSX.Element;
  notificationModal: boolean;
  rightCorner: string;
}

/**
 * Unapproved stripe payouts components
 * @returns {any} The rendered component
 */
function UnapprovedCPOPayouts() {
  const { t } = useTranslation('stripePayout');
  const dispatch = useAppDispatch();
  const {
    payouts,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    filterListLoading,
    filters,
    isFilterUpdated,
    sortField,
    sortBy,
  } = useAppSelector((state) => state.unapprovedStripePayouts);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<ModalBoxDetail>({
    title: '',
    content: <div />,
    rightCorner: '',
    notificationModal: false,
  });

  /**
   * Handle the fetch the data in api function
   * @param {any} data - The request data
   */
  const fetchData = (data: RequestStripePayoutsInterface) => {
    if (payouts.length === 0 || !loading) {
      dispatch(fetchUnapprovedStripePayouts(data));
    }
  };

  /**
   * Handle the auto refresh data function
   */
  const handleAutoRefresh = () => {
      const queryParameters = GetParametersFromCommonFilters(filters);
  
      dispatch(
        refreshUnapprovedStripePayouts({
          ...queryParameters,
          page,
          limit,
          sortBy,
          sortField,
        }),
      );
    };

  /**
   * Table pagination function
   * @param {any} value - The request data
   */
  const pageNateLimitChange = (value: RequestStripePayoutsInterface) => {
    const queryParameters = GetParametersFromCommonFilters(filters);

    fetchData({ ...queryParameters, ...value, sortField, sortBy });
  };

  /**
   * Handling approve warning
   * @param {string} id - The payout ID
   * @param {any} event - The mouse event
   */
  const handleApproveWarning = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      content: (
        <ApprovedContent
          id={id}
          setModalBoxStatus={setModalBoxStatus}
          setModalBoxDetail={setModalBoxDetail}
        />
      ),
      notificationModal: true,
    });
  };

    /**
     * Export data pdf or csv function
     * @param {string} type - The type of export
     */
    const exportDataPdfOrCSV = (type: string) => {
      const headers = [
        t('accountID'),
        t('accountName'),
        t('email'),
        t('revenueFromDate'),
        t('revenueToDate'),
        t('payoutID'),
        t('payoutAmount'),
        t('status'),
        t('payoutDate'),
      ];
      const rows = payouts.map((row: StripePayoutsInterface) => [
        row.accountCustomId || 'N/A',
        row.fromDate ? ConvertUTCtoDate(row.fromDate) : t('na'),
        row.toDate ? ConvertUTCtoDate(row.toDate) : t('na'),
        row.amount ? ToFixedValue(row.amount) : '0.00',
        row.status ? capitalizeFirstLetter(row.status) : t('na'),
        row.transferredAt ? ConvertUTCtoDate(row.transferredAt) : t('na'),
      ]);
      if (rows.length > 0) {
        if (type === 'csv') {
          exportCSV(headers, rows, 'stripe-payouts.csv');
        } else {
          exportPDF(headers, rows, 'stripe-payouts.pdf');
        }
      }
    };

  /**
   * Handling reject warning
   * @param {string} id - The payout ID
   * @param {any} event - The mouse event
   */
  const handleRejectWarning = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setModalBoxStatus('open');
    setModalBoxDetail({
      title: '',
      rightCorner: '',
      content: (
        <RejectedContext
          id={id}
          setModalBoxStatus={setModalBoxStatus}
          setModalBoxDetail={setModalBoxDetail}
        />
      ),
      notificationModal: true,
    });
  };

  const column = [
    {
      label: t('accountID'),
      accessorKey: 'accountCustomId' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('email'),
      accessorKey: 'email' as keyof StripePayoutsInterface,
      sortAllow: false,
    },
    {
      label: t('revenueFromDate'),
      accessorKey: 'fromDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('revenueToDate'),
      accessorKey: 'toDate' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('payoutAmount'),
      accessorKey: 'amount' as keyof StripePayoutsInterface,
      sortAllow: true,
      /**
       * Payment amount view in table
       * @param {any} payoutsStatus - The payout status
       * @returns {any} The rendered component
       */
      customData: (payoutsStatus: StripePayoutsInterface) => (
        <div>{ToFixedValue(payoutsStatus.amount)}</div>
      ),
    },
    {
      label: t('payoutDate'),
      accessorKey: 'createdAt' as keyof StripePayoutsInterface,
      sortAllow: true,
    },
    {
      label: t('action'),
      accessorKey: '' as keyof StripePayoutsInterface,
      columnWidth: 65,
      /**
       * Function for action buttons
       * @param {any} payoutsData - The payout data
       * @returns {any} The rendered component
       */
      customData: (payoutsData: StripePayoutsInterface) => (
        <div className="action-button-group">
          <div className="action-button-item view-color">
            <ActionApprove onClick={(event) => handleApproveWarning(payoutsData._id, event)} />
          </div>
          <div className="action-button-item view-color">
            <ActionReject
              role="presentation"
              onClick={(event) => handleRejectWarning(payoutsData._id, event)}
            />
          </div>
        </div>
      ),
    },
  ];

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({ ...queryParameters, page, limit, sortField, sortBy });
  };

  useEffect(() => {
    if (isFilterUpdated || payouts.length === 0) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
      dispatch(disableUnapprovedStripePayoutsApplied());
    }
  }, [filters]);

  useEffect(() => {
    dispatch(
      setPageTitle({
        title: t('cpoPayouts'),
        icon: <StripePayoutsIcon />,
        count: totalResults,
      }),
    );
  }, [totalResults]);

  return (
    <div className="table-container">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        rightCorner={modalBoxDetail.rightCorner}
        notification={modalBoxDetail.notificationModal}
        header={false}
      />
      <CustomTable
        header={column}
        version="v2"
        data={payouts}
        serialNo
        created
        lastUpdated
        limit={limit}
        page={page}
        loading={loading}
        totalPages={totalPages}
        totalResults={totalResults}
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageNateLimitChange}
        sizeChangeButtonRequired
        filters={filters}
        filtersToDispatch={updateUnapprovedStripePayoutsFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchUnapprovedStripePayoutsMethodFilters}
        requiredRefreshToggle
        refreshFunction={handleAutoRefresh}
        refreshRequired
        refershCurrentPages={handleRefresh}
        handleRefresh={handleRefresh}
                exportDataPdfOrCSV={permissionCheck('cpoCpoPayoutsExport') ? exportDataPdfOrCSV : undefined}
        
        hoverLabel="Refresh"
        height={320}
      />
    </div>
  );
}

export default UnapprovedCPOPayouts;
